// @tennis-matches/services/persistence/remote
// Remote persistence service (Supabase + DB operations)
// NOTE: getState import from legacy store - will be refactored in Phase 4
import {
  getSupabaseClient,
  getSupabaseUser,
  getPhase1TenantId,
  dbRepository,
} from "../supabase/index.js";

// TODO: Refactor getState dependency in Phase 4 (State Management Migration)
// For now, we import from the legacy store location
// This will be replaced when migrating to Zustand
import { getState } from "@tennis-matches/core/store";

import { recomputeStats } from "../scoring/index.js";
import {
  sortStatsRows,
  upsertTournamentRankingByCode,
} from "../ranking/index.js";
import {
  clearLocalState,
  loadState,
  saveStateLocal,
  sanitizeForSave,
  migrate,
} from "./localState.js";

export { clearLocalState, loadState };

// Supabase tables (Phase 1: JSONB snapshot)
const SB_TABLE_STATE = "app_states";
const SB_TABLE_MEMBERSHIPS = "tenant_memberships";
const SB_TABLE_TENANTS = "tenants";
const SB_TABLE_TOURNAMENT_BOOTSTRAP = "tournaments_bootstrap";

// PostgreSQL error codes
const PG_ERROR_UNDEFINED_COLUMN = "42703";
const HTTP_ERROR_BAD_REQUEST = "400";
// PostgREST error codes (schema cache / unknown field / unknown parameter)
const PGRST_ERROR_SCHEMA_CACHE_MISS = "PGRST204";
const PGRST_ERROR_FUNCTION_NOT_FOUND = "PGRST202";
const PGRST_ERROR_AMBIGUOUS_OVERLOAD = "PGRST203";

// Tournament defaults
const DEFAULT_TOURNAMENT_KIND = "schleifchen";
const DEFAULT_SOURCE = "app";

// Timing constants
const DEFAULT_DEBOUNCE_MS = 500;

function __isSchemaMismatchError(err) {
  const c = __asStr(err?.code);
  // PGRST204: schema cache miss / unknown column/param; 42703: undefined_column; 400: often used by PostgREST for bad req
  // PGRST202: function not found (often wrong function name or wrong arg signature)
  return (
    c === PGRST_ERROR_SCHEMA_CACHE_MISS ||
    c === PGRST_ERROR_FUNCTION_NOT_FOUND ||
    c === PGRST_ERROR_AMBIGUOUS_OVERLOAD ||
    c === PG_ERROR_UNDEFINED_COLUMN ||
    c === HTTP_ERROR_BAD_REQUEST
  );
}

async function __rpcUpsertTournamentBootstrapWithAuditBestEffort(
  supabase,
  { code, kind, payload, source, requestId, actorId, tenantId } = {},
) {
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  // Function name may differ across environments; try common variants.
  const fnNames = [
    "upsert_tournament_bootstrap_with_audit",
    "upsert_tournament_bootstrap",
  ];

  const attempts = [];

  // Most likely current/expected naming (with tenant_id)
  attempts.push({
    p_code: c,
    p_payload: payload,
    p_kind: __asStr(kind) || DEFAULT_TOURNAMENT_KIND,
    p_source: __asStr(source) || DEFAULT_SOURCE,
    ...(requestId ? { p_request_id: requestId } : {}),
    ...(actorId ? { p_actor: actorId } : {}),
    ...(tenantId ? { p_tenant_id: tenantId } : {}),
  });

  // Variant: function expects p_actor (without tenant_id for backward compatibility)
  attempts.push({
    p_code: c,
    p_payload: payload,
    p_kind: __asStr(kind) || DEFAULT_TOURNAMENT_KIND,
    p_actor: __asStr(actorId) || null,
    p_source: __asStr(source) || DEFAULT_SOURCE,
    ...(requestId ? { p_request_id: requestId } : {}),
  });

  // Variant: bare names (some setups expose functions with non-prefixed args)
  attempts.push({
    code: c,
    payload,
    kind: __asStr(kind) || DEFAULT_TOURNAMENT_KIND,
    source: __asStr(source) || DEFAULT_SOURCE,
    ...(requestId ? { request_id: requestId } : {}),
    ...(actorId ? { actor: actorId } : {}),
    ...(tenantId ? { tenant_id: tenantId } : {}),
  });

  // Variant: payload parameter name differs
  attempts.push({
    p_code: c,
    p_state: payload,
    p_kind: __asStr(kind) || DEFAULT_TOURNAMENT_KIND,
    p_source: __asStr(source) || DEFAULT_SOURCE,
    ...(requestId ? { p_request_id: requestId } : {}),
  });

  let last = null;
  for (const fnName of fnNames) {
    for (const rpcArgs of attempts) {
      const { data, error } = await supabase.rpc(fnName, rpcArgs);
      if (!error) return { ok: true, data };
      last = error;
      // Only retry other arg-shapes/fnNames when it looks like a signature/schema mismatch.
      if (!__isSchemaMismatchError(error)) return { ok: false, error: last };
    }
  }

  return { ok: false, error: last };
}

async function __tableUpsertTournamentBootstrapBestEffort(
  supabase,
  { code, kind, payload, actorId, tenantId } = {},
) {
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  // Canonical Phase 0/1 schema. We keep this intentionally strict to avoid 400/404 spam in the client.
  const tableNames = [SB_TABLE_TOURNAMENT_BOOTSTRAP];
  const codeCols = ["code"];
  const payloadCols = ["payload"];
  const kindCols = ["kind"];

  const tenantCols = ["tenant_id"];
  const actorCols = ["created_by"];

  const tryUpsert = async ({ table, row, onConflict }) => {
    try {
      const opts = onConflict ? { onConflict: onConflict } : undefined;
      const { error } = await supabase.from(table).upsert(row, opts);
      if (!error) return { ok: true, table, onConflict, row };
      return { ok: false, error };
    } catch (e) {
      return { ok: false, error: e };
    }
  };

  // Try minimal-first to avoid unknown-column errors.
  // IMPORTANT: Always include tenant_id and created_by if available to satisfy RLS policies
  for (const table of tableNames) {
    for (const codeCol of codeCols) {
      for (const payloadCol of payloadCols) {
        for (const kindCol of kindCols) {
          // 1) With tenant_id and created_by (preferred for RLS)
          {
            const row = {
              [codeCol]: c,
              [payloadCol]: payload,
              [kindCol]: __asStr(kind) || DEFAULT_TOURNAMENT_KIND,
            };
            // Always try to add tenant_id and created_by if available
            for (const tcol of tenantCols) {
              if (tenantId) row[tcol] = tenantId;
            }
            for (const acol of actorCols) {
              if (actorId) row[acol] = actorId;
            }
            const r1 = await tryUpsert({ table, row, onConflict: codeCol });
            if (r1.ok) return r1;
            // If it's not a schema-mismatch, no need to brute-force more shapes on this path.
            if (!__isSchemaMismatchError(r1.error)) {
              return r1;
            }
          }

          // 2) Minimal: code + payload (+ kind) without tenant/actor (fallback)
          {
            const row = {
              [codeCol]: c,
              [payloadCol]: payload,
              [kindCol]: __asStr(kind) || DEFAULT_TOURNAMENT_KIND,
            };
            const r1 = await tryUpsert({ table, row, onConflict: codeCol });
            if (r1.ok) return r1;
            if (!__isSchemaMismatchError(r1.error)) return r1;
          }

          // 3) Minimal: code + payload (no kind)
          {
            const row = { [codeCol]: c, [payloadCol]: payload };
            // Try with tenant/actor first
            for (const tcol of tenantCols) {
              if (tenantId) row[tcol] = tenantId;
            }
            for (const acol of actorCols) {
              if (actorId) row[acol] = actorId;
            }
            const r1 = await tryUpsert({ table, row, onConflict: codeCol });
            if (r1.ok) return r1;
            if (!__isSchemaMismatchError(r1.error)) return r1;
          }
        }
      }
    }
  }

  return {
    ok: false,
    reason: "db-error",
    error: { message: "All bootstrap table upsert variants failed" },
  };
}

// ---- Phase 1: fake admin + fake tenant (no auth required)
const PHASE1_MODE = true;
const PHASE1_ADMIN_ID = "1000001";
const PHASE1_TENANT_ID = "2000001";

const TENANT_LS_KEY = "ttb_active_tenant_id";

/**
 * Saves application state to localStorage and remote storage (best-effort).
 * @param {Object} state - The application state to save
 * @returns {void}
 */
export function saveState(state) {
  // Local first (works offline + fast boot)
  const clean = saveStateLocal(state);

  // Remote best-effort (if configured + logged in)
  // Note: intentionally fire-and-forget; offline/no-session should not break UX.
  saveRemoteState(clean);
}

// --- supabase persistence (phase 1) ---------------------------------

function __buildRankingSnapFromState(
  state,
  { matchKey, resultData, resultsOverride } = {},
) {
  const st = state && typeof state === "object" ? state : {};
  const mod = st?.modules?.schleifchen || {};
  const tournament = mod?.tournament || {};
  const ui = mod?.ui || {};

  const scheduleSource = Array.isArray(mod?.schedule) ? mod.schedule : [];

  // results: prefer explicit override (DB), otherwise state
  const resultsById =
    resultsOverride && typeof resultsOverride === "object"
      ? { ...resultsOverride }
      : mod?.results && typeof mod.results === "object"
        ? { ...mod.results }
        : {};

  // ensure just-saved result is included even if state lags behind
  try {
    const mk = String(matchKey || "").trim();
    if (mk && resultData && typeof resultData === "object") {
      resultsById[mk] = resultData;
    }
  } catch (_) {}

  const playerCount = Number(st?.shared?.players?.count || 0);
  if (!Number.isFinite(playerCount) || playerCount <= 0) return null;

  const pointsCfg = tournament?.points || {};
  const winPts = Number(pointsCfg?.win ?? 2);
  const drawPts = Number(pointsCfg?.draw ?? 1);
  const points = {
    win: Number.isFinite(winPts) ? winPts : 2,
    draw: Number.isFinite(drawPts) ? drawPts : 1,
  };

  const resultRules = mod?.resultRules || tournament?.resultRules || {};

  const statsMap = recomputeStats({
    schedule: scheduleSource,
    results: resultsById,
    points,
    playerCount,
    resultRules,
    currentRound: Number.isFinite(Number(ui?.round)) ? Number(ui.round) : 1,
    completedRound: Number.isFinite(Number(ui?.completedRound))
      ? Number(ui.completedRound)
      : 0,
  });

  const ordered = sortStatsRows(statsMap);

  return {
    v: 1,
    points,
    total: playerCount,
    ordered,
    statsMap,
    meta: { computedAt: Date.now() },
  };
}

function __resolveTournamentCodeForDb(inputTournamentId) {
  const direct = String(inputTournamentId || "").trim();

  // If it's already a short share code, keep it.
  // (Your codes are typically 6 chars, alnum.)
  if (/^[A-Z0-9]{4,12}$/i.test(direct) && !direct.includes("-")) return direct;

  // Otherwise, try to read the current tournament meta.code from store
  try {
    const st = typeof getState === "function" ? getState() : null;
    const code = String(
      st?.modules?.schleifchen?.tournament?.meta?.code || "",
    ).trim();
    if (code) return code;
  } catch (_) {}

  // Fallback: use whatever we got
  return direct;
}

import { isTrainingsDeskPathname } from "@tennis-matches/core";

/**
 * Trainerstunden-Hub/App: keine relationalen Schleifchen-Nachläufe (DB-Tabellen anderer Apps).
 * Nur URL-Pfad — keine Imports aus apps/*.
 */
function __skipSchleifchenRelationalHydrationFromLocation() {
  if (typeof window === "undefined") return false;
  try {
    if (isTrainingsDeskPathname(window.location.pathname)) return true;
  } catch (_) {}
  return false;
}

// Call once after initStore() to hydrate from Supabase (best-effort).
export function initRemotePersistence({ setState } = {}) {
  if (_remoteInitStarted) return;
  _remoteInitStarted = true;

  // Run in background (do not block UI)
  Promise.resolve()
    .then(async () => {
      if (typeof setState !== "function") return;
      const remoteWrap = await loadRemoteState();
      if (!remoteWrap || !remoteWrap.state) return;

      const remote = remoteWrap.state;
      const remoteUpdatedAt = remoteWrap.updatedAt;
      const local = typeof getState === "function" ? getState() : null;

      // Determine local freshness (prefer stamped __persist.localSavedAt)
      const localSavedAt =
        local &&
        local.__persist &&
        typeof local.__persist.localSavedAt === "number"
          ? local.__persist.localSavedAt
          : 0;

      // Remote freshness (updated_at from DB)
      const remoteSavedAt = remoteUpdatedAt
        ? Date.parse(String(remoteUpdatedAt))
        : 0;

      // Gate: Only hydrate from DB if remote is newer OR local has no meaningful data
      const localHasSchedule = !!(
        local?.modules?.schleifchen?.schedule &&
        local.modules.schleifchen.schedule.length
      );
      const localHasResults = !!(
        local?.modules?.schleifchen?.results &&
        Object.keys(local.modules.schleifchen.results || {}).length
      );
      const localLooksEmpty = !localHasSchedule && !localHasResults;

      const shouldHydrate =
        localLooksEmpty ||
        (remoteSavedAt && remoteSavedAt > localSavedAt) ||
        (remoteSavedAt === 0 && localSavedAt === 0 && localLooksEmpty);

      if (!shouldHydrate) {
        console.log(
          "[Remote-Sync] Skip hydration (local newer). localSavedAt=",
          localSavedAt,
          "remoteSavedAt=",
          remoteSavedAt,
        );
        return;
      }

      console.log(
        "[Remote-Sync] Hydrating from DB. localSavedAt=",
        localSavedAt,
        "remoteSavedAt=",
        remoteSavedAt,
      );

      // NEU: Relationale Daten für das aktuelle MatchMixer nachladen
      // IMPORTANT: DB tables are keyed by tournament CODE (TEXT), not UUID.
      const tournamentIdRaw =
        remote.modules?.schleifchen?.tournament?.internalId;
      const codeForDb = __resolveTournamentCodeForDb(tournamentIdRaw);

      if (codeForDb && !__skipSchleifchenRelationalHydrationFromLocation()) {
        // Ensure shared.players exists (defensive)
        remote.shared ??= {};
        remote.shared.players ??= { count: 0, names: {} };

        // 1) Players: ALWAYS load (even if no results exist yet)
        try {
          const dbPlayers = await dbRepository.getTournamentPlayers(codeForDb);
          if (Array.isArray(dbPlayers) && dbPlayers.length > 0) {
            const namesMap = {};
            dbPlayers.forEach((p) => {
              namesMap[String(p.seed)] = p.name;
            });

            remote.shared.players.names = namesMap;
            remote.shared.players.count = dbPlayers.length;

            console.log("[Remote-Sync] Relationale Spielerliste geladen.");
          }
        } catch (e) {
          console.warn(
            "[Remote-Sync] Fehler beim Nachladen der Spielerliste:",
            e,
          );
        }

        // 2) Schedule: Load from relational tables if missing in snapshot
        try {
          const snapshotSchedule = remote.modules?.schleifchen?.schedule;
          const hasSnapshotSchedule =
            Array.isArray(snapshotSchedule) && snapshotSchedule.length > 0;

          console.log(`[Remote-Sync] Schedule-Check für ${codeForDb}:`, {
            hasSnapshotSchedule,
            snapshotLength: snapshotSchedule?.length || 0,
          });

          if (!hasSnapshotSchedule) {
            console.log(
              `[Remote-Sync] Lade Spielplan aus relationalen Tabellen für ${codeForDb}...`,
            );
            const dbSchedule = await dbRepository.getFullSchedule(codeForDb);
            console.log(`[Remote-Sync] getFullSchedule Ergebnis:`, {
              isArray: Array.isArray(dbSchedule),
              length: dbSchedule?.length || 0,
              sample: dbSchedule?.[0],
            });

            if (Array.isArray(dbSchedule) && dbSchedule.length > 0) {
              // Convert DB format to app format
              // getFullSchedule returns: [{ round: 1, matches: [{ id, court, A, B, type }] }]
              // App expects: [[{ id, court, A, B }], [{ id, court, A, B }], ...]
              const schedule = dbSchedule
                .sort((a, b) => (a.round || 0) - (b.round || 0))
                .map((r) => {
                  const roundNo = Number(r.round || 0);
                  const matches = (r.matches || []).map((m, idx) => {
                    // WICHTIG: id muss mit dem Key übereinstimmen, der beim Laden der Ergebnisse verwendet wird
                    // Falls id nicht vorhanden, generiere aus roundNo und match_no oder Index
                    const matchNo = Number(m.match_no || idx + 1);
                    const matchKey =
                      m.id ||
                      (roundNo > 0 && matchNo > 0
                        ? `r${roundNo}m${matchNo}`
                        : null);
                    return {
                      id: matchKey, // WICHTIG: Muss mit Ergebnis-Keys übereinstimmen
                      court: m.court || "",
                      A: Array.isArray(m.A) ? m.A : [],
                      B: Array.isArray(m.B) ? m.B : [],
                      type:
                        m.type ||
                        (m.A?.length === 1 && m.B?.length === 1
                          ? "singles"
                          : "doubles"),
                    };
                  });
                  return matches;
                });

              remote.modules.schleifchen.schedule = schedule;
              console.log(
                `[Remote-Sync] ✅ Relationaler Spielplan geladen: ${schedule.length} Runden, ${schedule.reduce((sum, r) => sum + r.length, 0)} Matches gesamt.`,
              );
            } else {
              console.warn(
                `[Remote-Sync] ⚠️ Kein Spielplan in DB gefunden für Turnier ${codeForDb}`,
              );
            }
          } else {
            // Schedule aus Snapshot - prüfe ob id-Properties vorhanden sind
            const firstMatch = snapshotSchedule[0]?.[0];
            const hasIds = firstMatch && firstMatch.id;
            console.log(
              `[Remote-Sync] ✅ Spielplan bereits im Snapshot vorhanden (${snapshotSchedule.length} Runden)`,
              {
                hasIds,
                firstMatchId: firstMatch?.id,
                firstMatchSample: firstMatch,
              },
            );

            // Falls keine id-Properties vorhanden, füge sie hinzu
            if (!hasIds && snapshotSchedule.length > 0) {
              console.log(
                `[Remote-Sync] ⚠️ Schedule aus Snapshot hat keine id-Properties - füge sie hinzu...`,
              );
              const scheduleWithIds = snapshotSchedule.map(
                (round, roundIdx) => {
                  const roundNo = roundIdx + 1;
                  return round.map((match, matchIdx) => {
                    const matchNo = matchIdx + 1;
                    return {
                      ...match,
                      id: match.id || `r${roundNo}m${matchNo}`,
                    };
                  });
                },
              );
              remote.modules.schleifchen.schedule = scheduleWithIds;
              console.log(
                `[Remote-Sync] ✅ id-Properties zum Schedule hinzugefügt`,
              );
            }
          }
        } catch (e) {
          console.error(
            "[Remote-Sync] ❌ Fehler beim Nachladen des Spielplans:",
            e,
          );
        }

        // 3) Results: optional load
        try {
          const resultsMap = await dbRepository.getMatchResults(codeForDb);
          if (
            resultsMap &&
            typeof resultsMap === "object" &&
            Object.keys(resultsMap).length > 0
          ) {
            // Ensure results are properly structured
            const normalizedResults = {};
            for (const [key, result] of Object.entries(resultsMap)) {
              if (result && typeof result === "object") {
                // Ensure sets array exists if result has data
                if (result.sets && !Array.isArray(result.sets)) {
                  console.warn(
                    `[Remote-Sync] Result ${key} has invalid sets:`,
                    result,
                  );
                }
                normalizedResults[key] = result;
                console.log(`[Remote-Sync] ✅ Result ${key}:`, {
                  hasSets: Array.isArray(result.sets),
                  setsCount: result.sets?.length || 0,
                  sets: result.sets,
                  fullResult: result,
                });
              } else {
                console.warn(
                  `[Remote-Sync] ⚠️ Result ${key} is not an object:`,
                  result,
                );
              }
            }
            remote.modules.schleifchen.results = normalizedResults;
            console.log(
              `[Remote-Sync] ✅ Relationale Match-Ergebnisse geladen: ${Object.keys(normalizedResults).length} Ergebnisse.`,
              {
                keys: Object.keys(normalizedResults),
                sample: normalizedResults[Object.keys(normalizedResults)[0]],
              },
            );
          } else {
            console.warn(
              `[Remote-Sync] ⚠️ Keine Ergebnisse gefunden für Turnier ${codeForDb}`,
              {
                resultsMapType: typeof resultsMap,
                resultsMapKeys: resultsMap ? Object.keys(resultsMap) : "null",
              },
            );
          }
        } catch (e) {
          console.error(
            "[Remote-Sync] ❌ Fehler beim Nachladen der Ergebnisse:",
            e,
          );
        }
      }

      // Store remote timestamp in state (optional)
      remote.__persist = {
        ...(remote.__persist || {}),
        remoteSavedAt: remoteSavedAt || 0,
      };

      // Snapshot + relationale Daten in den State setzen
      setState(() => remote);

      // Mirror back to localStorage for fast next boot.
      try {
        saveStateLocal(remote);
      } catch {
        // ignore
      }
    })
    .catch(() => {
      // ignore remote errors (offline, no session, etc.)
    });
}

// -------------------------------------------------------------------
// Relational hydration (Phase 1): Schleifchen aus DB laden
// - Source of truth: tournaments + tournament_players + tournament_rounds/matches + match_results
// - We MERGE into current state (do not replace entire app state snapshot).
// -------------------------------------------------------------------

function __asInt(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
}

function __asStr(v) {
  return String(v ?? "").trim();
}

function __buildScheduleFromDb(latest) {
  const rounds = Array.isArray(latest?.tournament_rounds)
    ? latest.tournament_rounds
    : [];
  rounds.sort((a, b) => __asInt(a?.round_no) - __asInt(b?.round_no));

  const schedule = [];

  for (const r of rounds) {
    const matches = Array.isArray(r?.tournament_matches)
      ? r.tournament_matches
      : [];
    matches.sort((a, b) => __asInt(a?.match_no) - __asInt(b?.match_no));

    const roundArr = matches.map((m) => {
      const court = __asStr(m?.court_no || "");
      const A = Array.isArray(m?.side_1?.players) ? m.side_1.players : [];
      const B = Array.isArray(m?.side_2?.players) ? m.side_2.players : [];
      return { court, A, B };
    });

    schedule.push(roundArr);
  }

  return schedule;
}

function __buildResultsMapFromDb(latest) {
  const rows = Array.isArray(latest?.match_results) ? latest.match_results : [];
  const rounds = Array.isArray(latest?.tournament_rounds)
    ? latest.tournament_rounds
    : [];

  // Build matchUuid -> rNmM key from hydrated rounds/matches
  const idToKey = {};
  for (const r of rounds) {
    const rno = Number(r?.round_no || 0) || 0;
    const ms = Array.isArray(r?.tournament_matches) ? r.tournament_matches : [];
    for (const m of ms) {
      const mno = Number(m?.match_no || 0) || 0;
      const mid = __asStr(m?.id);
      if (!mid || rno <= 0 || mno <= 0) continue;
      idToKey[mid] = `r${rno}m${mno}`;
    }
  }

  const out = {};
  for (const r of rows) {
    const mid = __asStr(r?.match_id);
    if (!mid) continue;
    const key = idToKey[mid];
    if (!key) continue;
    out[key] = r?.result || {};
  }
  return out;
}

function __buildPlayersFromDb(latest) {
  const rows = Array.isArray(latest?.tournament_players)
    ? latest.tournament_players
    : [];
  rows.sort((a, b) => __asInt(a?.seed) - __asInt(b?.seed));

  const names = {};
  const playersArr = [];

  for (const p of rows) {
    const seed = __asInt(p?.seed);
    const name = __asStr(p?.name);
    if (!seed) continue;

    names[String(seed)] = name;

    // Phase 1: pid = seed (stable & simple).
    playersArr.push({ name, pid: String(seed) });
  }

  return { count: Math.max(0, playersArr.length), names, playersArr };
}

// Public: loads latest tournament from DB and merges into current state
export async function hydrateSchleifchenFromDb({ setState } = {}) {
  if (typeof setState !== "function") return false;

  try {
    const latest = await dbRepository.getLatestTournament();
    if (!latest?.id) return false;

    const tournamentId = __asStr(latest.id);
    const format = __asStr(latest?.format) || "doubles";
    const rounds = __asInt(latest?.rounds, 6);
    const minutesPerRound = __asInt(latest?.minutes_per_round, 15);

    const winPts = Number(latest?.points_win);
    const drawPts = Number(latest?.points_draw);

    const points = {
      win: Number.isFinite(winPts) ? winPts : 2,
      draw: Number.isFinite(drawPts) ? drawPts : 1,
    };

    const schedule = __buildScheduleFromDb(latest);
    const results = __buildResultsMapFromDb(latest);
    const p = __buildPlayersFromDb(latest);

    const currentRound = __asInt(latest?.current_round, 1);
    const phase = __asStr(latest?.phase) || "idle";

    setState((prev) => {
      const mod = prev?.modules?.schleifchen || {};
      const prevT = mod.tournament || {};

      return {
        ...prev,
        shared: {
          ...prev.shared,
          players: {
            ...(prev.shared?.players || {}),
            count: p.count || (prev.shared?.players?.count ?? 0),
            names: Object.keys(p.names || {}).length
              ? p.names
              : prev.shared?.players?.names || {},
          },
        },
        modules: {
          ...prev.modules,
          schleifchen: {
            ...mod,
            tournament: {
              ...prevT,
              internalId: tournamentId,
              format,
              rounds,
              minutesPerRound,
              points,
              // keep meta if present; DB can later carry meta too
              meta: { ...(prevT.meta || {}) },
              players:
                Array.isArray(p.playersArr) && p.playersArr.length
                  ? p.playersArr
                  : prevT.players || [],
            },
            schedule: Array.isArray(schedule) ? schedule : mod.schedule || [],
            results:
              results && Object.keys(results).length
                ? results
                : mod.results || {},
            // DEBUG: Log if results are being set
            ...(results && Object.keys(results).length > 0
              ? { __debugResultsLoaded: Object.keys(results).length }
              : {}),
            // keep flow/ui mostly intact; we at least align ui to DB status
            ui: {
              ...(mod.ui || {}),
              round: currentRound || (mod.ui?.round ?? 1),
              phase: phase || (mod.ui?.phase ?? "idle"),
            },
          },
        },
      };
    });

    console.log(
      "[Remote-Hydration] Schleifchen-Turnier aus DB geladen:",
      tournamentId,
    );
    return true;
  } catch (e) {
    console.warn("[Remote-Hydration] hydrateSchleifchenFromDb failed:", e);
    return false;
  }
}

async function ensureTenantId(supabase, userId) {
  // 1. Bestehende Auswahl aus LS prüfen
  let tid = localStorage.getItem(TENANT_LS_KEY);

  // 2. Alle Mitgliedschaften des Users laden
  const userTenants = await dbRepository.getUserTenants(userId);

  if (userTenants.length > 0) {
    // Falls tid im LS valide ist, behalten
    const currentValid = userTenants.find((t) => t.tenant_id === tid);
    if (currentValid) return tid;

    // Sonst den ersten verfügbaren Tenant nehmen
    const firstTid = userTenants[0].tenant_id;
    localStorage.setItem(TENANT_LS_KEY, firstTid);
    return firstTid;
  }

  // 3. Fallback: Neuen Tenant erstellen, falls noch gar keiner existiert
  const newTenant = await dbRepository.createTenant(userId, `Mein Verein`);
  if (newTenant) {
    localStorage.setItem(TENANT_LS_KEY, newTenant.id);
    return newTenant.id;
  }
  return "";
}

async function loadRemoteState() {
  const ctx = await __getRemoteCtx();
  if (!ctx) return null;

  const { supabase, userId, tenantId } = ctx;

  const { data, error } = await supabase
    .from(SB_TABLE_STATE)
    .select("state, updated_at")
    .eq("tenant_id", tenantId)
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (error) return null;
  const raw = data?.state || null;
  if (!raw) return null;

  const migrated = migrate(raw);
  return {
    state: migrated,
    updatedAt: data?.updated_at || null,
    tenantId,
    userId,
  };
}

async function saveRemoteState(cleanState) {
  // Phase 1: do not write app_states snapshots (avoid 400 spam / missing unique constraints)
  if (PHASE1_MODE) return;

  const ctx = await __getRemoteCtx();
  if (!ctx) return;

  try {
    await __saveAppStateSnapshotDirect(ctx, cleanState);
  } catch (e) {
    console.warn("[Remote] saveRemoteState failed (will ignore):", e);
  }
}

async function __saveAppStateSnapshotDirect(ctx, cleanState) {
  const supabase = ctx?.supabase || (await getSupabaseClient());
  if (!supabase) throw new Error("no-supabase");

  // Try the "intended" schema first: tenant_id + user_id + state
  // If the table/constraint doesn't match, fall back to a single "id" key.
  const tenantId = String(ctx.tenantId || "").trim();
  const userId = String(ctx.userId || "").trim();

  // 1) Preferred: composite upsert (requires columns + unique constraint)
  try {
    const { error } = await supabase
      .from(SB_TABLE_STATE)
      .upsert(
        { tenant_id: tenantId, user_id: userId, state: cleanState },
        { onConflict: "tenant_id,user_id" },
      );
    if (!error) return true;

    // If Postgres complains about missing columns/constraints, continue to fallback.
    const code = String(error?.code || "");
    if (code && code !== PG_ERROR_UNDEFINED_COLUMN) {
      // PG_ERROR_UNDEFINED_COLUMN = undefined_column; other errors might still be recoverable, but we try fallback anyway.
      // continue
      // continue to fallback
    }
  } catch (e) {
    console.warn(
      "[persistence] Failed to save state with composite key, falling back to single key:",
      e,
    );
    // continue to fallback below
  }

  // 2) Fallback: single primary key "id" (common minimal schema)
  const id = `${tenantId || "t"}:${userId || "u"}`;
  const { error: e2 } = await supabase
    .from(SB_TABLE_STATE)
    .upsert({ id, state: cleanState }, { onConflict: "id" });

  if (e2) throw e2;
  return true;
}

// -------------------------------------------------------------------
// Remote domain operations (ONLY call Supabase/Repository from here)
// UI/Pages/Controllers must NOT import dbRepository directly.
// -------------------------------------------------------------------

async function __getRemoteCtx() {
  // Phase 1: Single-tenant mode
  // - tenantId must ALWAYS exist (fallback to PHASE1 tenant)
  // - userId optional
  try {
    const ctx =
      dbRepository && typeof dbRepository.getContext === "function"
        ? dbRepository.getContext() || {}
        : {};

    let tenantId = String(ctx.tenantId || "").trim();
    let userId = String(ctx.userId || "").trim() || null;

    // Hard fallback for Phase 1
    if (!tenantId) tenantId = "2000001";

    // Make it visible for debugging / legacy callers
    try {
      if (typeof localStorage !== "undefined") {
        const cur = localStorage.getItem("ttb_active_tenant_id");
        if (!cur) localStorage.setItem("ttb_active_tenant_id", tenantId);
      }
    } catch {}

    return { tenantId, userId };
  } catch (e) {
    console.warn("[Remote] __getRemoteCtx fallback:", e);
    return { tenantId: "2000001", userId: null };
  }
}

export async function saveTournamentSetupRemote({
  tournament,
  schedule,
  players,
} = {}) {
  try {
    // Save players to players table BEFORE saving tournament setup
    // This ensures players are available for future autocomplete suggestions
    if (players && Array.isArray(players) && players.length > 0) {
      const { addPlayers } = await import("../players/index.js");
      const playerNames = players
        .map((p) => (typeof p === "string" ? p : p?.name))
        .filter(Boolean)
        .filter((name) => name.trim() && name.trim() !== "(Optional)");

      if (playerNames.length > 0) {
        console.log(
          "[Remote] Saving",
          playerNames.length,
          "players to players table before tournament setup",
        );
        try {
          await addPlayers(playerNames);
          console.log("[Remote] Successfully saved players to players table");
        } catch (playerErr) {
          console.warn(
            "[Remote] Failed to save players to players table (non-critical):",
            playerErr,
          );
          // Don't fail the entire operation if player saving fails
        }
      }
    }

    const res = await dbRepository.saveTournamentSetup({
      tournament,
      schedule,
      players,
    });
    if (res && res.ok) {
      console.log(
        "[Remote] saveTournamentSetupRemote success:",
        res.tournamentId,
      );
      return { ok: true, tournamentId: res.tournamentId };
    } else {
      console.error(
        "[Remote] saveTournamentSetupRemote failed:",
        res?.reason || "unknown",
        res?.error,
      );
      return { ok: false, reason: res?.reason, error: res?.error };
    }
  } catch (e) {
    console.error("[Remote] saveTournamentSetupRemote exception:", e);
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * Atomare Schedule-/Runden-Speicherung (MM-10).
 * @returns {Promise<{ok: boolean, reason?: string, error?: unknown, data?: object}>}
 */
export async function saveMatchmixerScheduleAtomic({
  code,
  schedule,
  players = null,
  results = null,
  currentRound = null,
  phase = null,
} = {}) {
  try {
    const tournamentCode = String(code || "").trim();
    if (!tournamentCode) {
      return { ok: false, reason: "missing-code" };
    }
    if (!Array.isArray(schedule)) {
      return { ok: false, reason: "schedule-must-be-array" };
    }
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return { ok: false, reason: "no-supabase" };
    }
    const { data, error } = await supabase.rpc(
      "matchmixer_save_schedule_atomic",
      {
        p_code: tournamentCode,
        p_schedule: schedule,
        p_players: players ?? null,
        p_results: results && typeof results === "object" ? results : {},
        p_current_round:
          currentRound == null || currentRound === ""
            ? null
            : Number(currentRound),
        p_phase: phase == null || phase === "" ? null : String(phase),
      },
    );
    if (error) {
      return { ok: false, reason: "rpc-error", error };
    }
    if (data && data.ok === false) {
      return { ok: false, reason: "rpc-rejected", data };
    }
    return { ok: true, data: data || null, tournamentId: tournamentCode };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function backfillTournamentMatchSidesFromLocalState() {
  try {
    const st = typeof getState === "function" ? getState() : null;
    const code = String(
      st?.modules?.schleifchen?.tournament?.meta?.code || "",
    ).trim();
    const schedule = st?.modules?.schleifchen?.schedule || [];
    if (!code || !Array.isArray(schedule) || !schedule.length) {
      console.warn("[Backfill] missing code/schedule");
      return { ok: false, reason: "missing-code-or-schedule" };
    }

    // We backfill only doubles matches with A/B arrays
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    // Load rounds for this tournament (tournament_id = code)
    const { data: rounds, error: rErr } = await supabase
      .from("tournament_rounds")
      .select("id, round_no")
      .eq("tournament_id", code);

    if (rErr) return { ok: false, reason: "db-error-rounds", error: rErr };

    const roundIdByNo = {};
    (rounds || []).forEach((r) => {
      roundIdByNo[String(r.round_no)] = r.id;
    });

    // For each schedule match: find tournament_match by (round_id, match_no) and update side_1/side_2
    for (let i = 0; i < schedule.length; i++) {
      const roundNo = i + 1;
      const round = schedule[i];
      if (!Array.isArray(round)) continue;

      const roundId = roundIdByNo[String(roundNo)];
      if (!roundId) continue;

      for (const m of round) {
        if (!m || typeof m !== "object") continue;
        if (String(m.type || "").toLowerCase() === "sitout") continue;

        const matchNo = Number(m.court || m.matchNo || 1);
        const A = Array.isArray(m.A) ? m.A : [];
        const B = Array.isArray(m.B) ? m.B : [];
        if (!A.length && !B.length) continue;

        // match_no is stored in tournament_matches.match_no
        const { error: uErr } = await supabase
          .from("tournament_matches")
          .update({ side_1: A, side_2: B })
          .eq("round_id", roundId)
          .eq("match_no", matchNo);

        if (uErr) {
          console.warn(
            "[Backfill] update failed round",
            roundNo,
            "match",
            matchNo,
            uErr,
          );
        }
      }
    }

    console.log("[Backfill] done for", code);
    return { ok: true, code };
  } catch (e) {
    console.warn("[Backfill] exception", e);
    return { ok: false, reason: "exception", error: e };
  }
}

export async function saveMatchResultRemote({
  tournamentId,
  matchKey,
  matchId,
  resultData,
} = {}) {
  const mk = String(matchKey || matchId || "").trim();
  if (!mk) {
    console.warn("[saveMatchResultRemote] Missing matchKey/matchId", {
      tournamentId,
      matchKey,
      matchId,
    });
    return false;
  }

  // IMPORTANT: DB tables match_results / tournament_rankings are keyed by tournament code (TEXT)
  const codeForDb = __resolveTournamentCodeForDb(tournamentId);
  if (!codeForDb) {
    console.warn("[saveMatchResultRemote] Could not resolve tournament code", {
      tournamentId,
    });
    return false;
  }

  const ctx = await __getRemoteCtx();
  if (!ctx?.tenantId) {
    console.warn("[saveMatchResultRemote] Missing tenantId", {
      tournamentId,
      codeForDb,
    });
    return false;
  }

  console.log("[saveMatchResultRemote] Saving match result", {
    tournamentId,
    codeForDb,
    matchKey: mk,
    hasResultData: !!resultData,
    resultDataKeys: resultData ? Object.keys(resultData) : [],
    tenantId: ctx.tenantId,
  });

  try {
    // 1) Write result into DB (source of truth)
    const ok = await dbRepository.saveMatchResult({
      tournamentId: codeForDb,
      tenantId: String(ctx.tenantId),
      matchKey: mk,
      result: resultData || {},
      userId: ctx.userId || null,
    });

    console.log("[saveMatchResultRemote] saveMatchResult returned", {
      tournamentId: codeForDb,
      matchKey: mk,
      ok,
    });

    // 2) Recompute + persist ranking snapshot from DB results (so it's never "one step behind")
    try {
      const st = typeof getState === "function" ? getState() : null;

      let dbResults = null;
      try {
        dbResults = await dbRepository.getMatchResults(codeForDb);
      } catch (e) {
        console.warn(
          `[persistence] Failed to load match results from DB for tournament "${codeForDb}":`,
          e,
        );
      }

      const snap = __buildRankingSnapFromState(st, {
        matchKey: mk,
        resultData,
        resultsOverride: dbResults,
      });

      if (snap) {
        await upsertTournamentRankingByCode(codeForDb, snap);
      }
    } catch (e) {
      console.warn(
        `[persistence] Failed to recompute and persist ranking snapshot for tournament "${codeForDb}":`,
        e,
      );
    }

    return !!ok;
  } catch (e) {
    console.warn("[Remote] saveMatchResultRemote failed:", e);
    return false;
  }
}

/**
 * Load i18n messages from Supabase (optional feature).
 * Keeps dbRepository usage out of app.js and pages.
 */
export async function loadI18nMessagesRemote(locale) {
  const loc = String(locale || "").trim();
  if (!loc) return {};

  const ctx = await __getRemoteCtx();
  if (!ctx) return {};

  try {
    const msgs = await dbRepository.getI18nMessages(loc);
    return msgs || {};
  } catch (e) {
    console.warn("[Remote] saveMatchResultRemote failed:", e);
    return {};
  }
}

// -------------------------------------------------------------------
// Remote helpers (traffic-controller API)

// -------------------------------------------------------------------
// tournaments_bootstrap traffic-controller (dedupe + singleflight)
// Goal: avoid writing the same snapshot multiple times per UI change.
// -------------------------------------------------------------------

// __BOOTSTRAP_CTL_V1__
const __bootstrapCtl = {
  byCode: new Map(), // code -> { lastHash, inflight, queuedTimer, queuedArgs }
};

// Real JS stable stringify + hash:
function __stableJson(val) {
  const seen = new WeakSet();
  const walk = (v) => {
    if (v === null || v === undefined) return v;
    const t = typeof v;
    if (t === "number" || t === "string" || t === "boolean") return v;
    if (t === "bigint") return String(v);
    if (t === "function") return undefined;
    if (t !== "object") return v;
    if (seen.has(v)) return "[Circular]";
    seen.add(v);
    if (Array.isArray(v)) return v.map(walk);
    const out = {};
    for (const k of Object.keys(v).sort()) {
      const nv = walk(v[k]);
      if (nv !== undefined) out[k] = nv;
    }
    return out;
  };
  try {
    return JSON.stringify(walk(val));
  } catch {
    return JSON.stringify(String(val));
  }
}

function __hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

async function __bootstrapUpsertSingleflight(
  code,
  runner,
  args,
  { debounceMs = DEFAULT_DEBOUNCE_MS } = {},
) {
  const c = String(code || "").trim();
  if (!c) return { ok: false, reason: "missing-code" };

  const entry = __bootstrapCtl.byCode.get(c) || {
    lastHash: "",
    inflight: null,
    queuedTimer: null,
    queuedArgs: null,
  };
  __bootstrapCtl.byCode.set(c, entry);

  const keyStr = __stableJson({
    kind: args?.kind,
    source: args?.source,
    payload: args?.payload,
  });
  const hash = __hashStr(keyStr);

  if (entry.lastHash && entry.lastHash === hash) {
    return { ok: true, code: c, skipped: true, reason: "dedupe" };
  }

  if (entry.inflight) {
    entry.queuedArgs = { ...(args || {}), __hash: hash };
    return { ok: true, code: c, queued: true };
  }

  if (entry.queuedTimer) clearTimeout(entry.queuedTimer);
  entry.queuedArgs = { ...(args || {}), __hash: hash };

  return await new Promise((resolve) => {
    entry.queuedTimer = setTimeout(async () => {
      entry.queuedTimer = null;
      const runArgs = entry.queuedArgs;
      entry.queuedArgs = null;

      entry.inflight = (async () => {
        try {
          const res = await runner(runArgs);
          if (res?.ok) entry.lastHash = runArgs?.__hash || hash;
          return res;
        } finally {
          entry.inflight = null;

          if (entry.queuedArgs) {
            const next = entry.queuedArgs;
            entry.queuedArgs = null;
            try {
              // debounce queued write to avoid double-writes after inflight
              await new Promise((r) => setTimeout(r, debounceMs));
              const res2 = await runner(next);
              if (res2?.ok) entry.lastHash = next?.__hash || entry.lastHash;
            } catch (e) {
              console.warn(
                "[persistence] Failed to process queued bootstrap upsert:",
                e,
              );
            }
          }
        }
      })();

      try {
        const out = await entry.inflight;
        resolve(out || { ok: false, reason: "no-result" });
      } catch (e) {
        resolve({ ok: false, reason: "exception", error: e });
      }
    }, debounceMs);
  });
}

function __isUuid(s) {
  const v = String(s || "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v,
  );
}

// UI/Pages/Controllers must NOT import dbRepository directly.
// -------------------------------------------------------------------

export async function saveMatchResultsRemote({
  tournamentId,
  resultsByMatchId,
} = {}) {
  const tid = String(tournamentId || "").trim();
  if (!tid) return false;
  const ctx = await __getRemoteCtx();
  if (!ctx) return false;
  const map = resultsByMatchId || {};
  try {
    const entries = [];
    for (const [matchId, result] of Object.entries(map)) {
      const mid = String(matchId || "").trim();
      if (!mid) continue;
      if (result == null) continue;
      entries.push({ match_id: mid, result });
    }
    if (!entries.length) return true;
    await dbRepository.upsertMatchResults(
      tid,
      ctx.tenantId,
      ctx.userId,
      entries,
    );
    return true;
  } catch (e) {
    console.warn("[Remote] saveMatchResultsRemote failed:", e);
    return false;
  }
}

export async function updateTournamentRemoteFieldsRemote({
  tournamentId,
  patch,
} = {}) {
  const tid = String(tournamentId || "").trim();
  if (!tid) return false;
  const ctx = await __getRemoteCtx();
  if (!ctx) return false;
  const p = patch && typeof patch === "object" ? patch : null;
  if (!p) return false;
  try {
    await dbRepository.updateTournamentFields(tid, ctx.tenantId, p);
    return true;
  } catch (e) {
    console.warn("[Remote] updateTournamentRemoteFieldsRemote failed:", e);
    return false;
  }
}

// Remote: tournament status (round/phase)
// NOTE: Needed because some pages import { updateTournamentStatusRemote } from this service.
export async function updateTournamentStatusRemote(
  tournamentId,
  roundNo,
  phase,
) {
  try {
    return await dbRepository.updateTournamentStatus(
      tournamentId,
      roundNo,
      phase,
    );
  } catch (e) {
    console.warn("[Remote] updateTournamentStatusRemote failed:", e);
    return false;
  }
}

/**
 * Phase 0/1: Write full payload by short tournament code WITH audit trail.
 * Uses Supabase RPC: upsert_tournament_bootstrap_with_audit(p_code, p_payload, p_kind, p_actor, p_source, p_request_id)
 *
 * Notes:
 * - No auth/user id yet: actor defaults to 'anon' in SQL.
 * - requestId is optional; DB default will generate a UUID if omitted.
 */

/**
 * Phase 0: Minimal DB connectivity check (legacy wrapper).
 * Keeps older imports working.
 *
 * Writes only the short tournament code into SB_TABLE_TOURNAMENT_BOOTSTRAP.
 * (No auth/tenant required)
 */

/**
 * Phase 0/1: Upsert full tournament snapshot by short code into tournaments_bootstrap.
 * NO auth/user required (for now). Audit trail is handled by DB trigger.
 *
 * tournaments_bootstrap columns (current):
 *   code (pk), kind, meta (jsonb), payload (jsonb),
 *   tournament_id (uuid), name (text), start_date (date),
 *   format (text), rounds (int), minutes_per_round (int),
 *   created_at, updated_at
 */
export async function upsertTournamentBootstrapRemote({
  code,
  kind = DEFAULT_TOURNAMENT_KIND,
  meta,
  payload,
  source = DEFAULT_SOURCE,
  requestId,
} = {}) {
  const tournamentCode = String(code || "").trim();
  if (!tournamentCode) return { ok: false, reason: "missing-code" };
  if (payload === undefined) return { ok: false, reason: "missing-payload" };

  // Legacy wrapper: route to audited RPC, but coalesce writes client-side.
  return __bootstrapUpsertSingleflight(
    tournamentCode,
    async (args) =>
      upsertTournamentBootstrapWithAuditRemote({
        code: tournamentCode,
        kind: args.kind,
        payload: args.payload,
        source: args.source,
        requestId: args.requestId,
      }),
    {
      kind: String(kind || DEFAULT_TOURNAMENT_KIND),
      payload,
      source: String(source || DEFAULT_SOURCE),
      requestId,
    },
    { debounceMs: DEFAULT_DEBOUNCE_MS },
  );
}
/**
 * Bootstraps a tournament with metadata and payload.
 * @param {Object} params - Parameters object
 * @param {string} params.code - Tournament code (required)
 * @param {string} [params.kind] - Tournament kind (default: "schleifchen")
 * @param {Object} [params.meta] - Tournament metadata
 * @param {any} [params.payload] - Tournament payload data
 * @returns {Promise<{ok: boolean, code?: string, eventId?: string, reason?: string}>}
 */
export async function bootstrapTournamentRemote({
  code,
  kind = DEFAULT_TOURNAMENT_KIND,
  meta = {},
  payload = null,
} = {}) {
  const tournamentCode = String(code || "").trim();
  if (!tournamentCode) return { ok: false, reason: "missing-code" };

  // Phase 0/1: Prefer audited upsert (includes dedupe/singleflight in the target).
  return upsertTournamentBootstrapWithAuditRemote({
    code: tournamentCode,
    kind,
    payload,
    source: "bootstrap",
  });
}

/**
 * Upserts a tournament bootstrap record with audit trail.
 * @param {Object} params - Parameters object
 * @param {string} params.code - Tournament code (required)
 * @param {string} [params.kind] - Tournament kind (default: "schleifchen")
 * @param {any} params.payload - Tournament payload data (required)
 * @param {string} [params.source] - Source of the update (default: "app")
 * @param {string} [params.requestId] - Optional request ID for tracking
 * @returns {Promise<{ok: boolean, code?: string, eventId?: string, reason?: string, fallback?: boolean|string, error?: any}>}
 */
export async function upsertTournamentBootstrapWithAuditRemote({
  code,
  kind = DEFAULT_TOURNAMENT_KIND,
  payload,
  source = DEFAULT_SOURCE,
  requestId,
} = {}) {
  const tournamentCode = String(code || "").trim();
  if (!tournamentCode) return { ok: false, reason: "missing-code" };
  if (payload === undefined) return { ok: false, reason: "missing-payload" };

  return __bootstrapUpsertSingleflight(
    tournamentCode,
    async (args) => {
      try {
        const supabase = await getSupabaseClient();
        // Best-effort actor + tenant context for tables that enforce created_by/tenant_id in RLS.
        let actorId = "";
        try {
          const { data: u1 } = await supabase.auth.getUser();
          actorId = String(u1?.user?.id || "").trim();
        } catch (_) {}

        // Try to reuse the phase1 tenant id (uuid) if available
        let tenantId = "";
        try {
          const t1 =
            typeof getPhase1TenantId === "function"
              ? String(getPhase1TenantId() || "").trim()
              : "";
          if (__isUuid(t1)) tenantId = t1;
        } catch (_) {}

        // Optional client-side requestId; DB default exists too.
        let rid = args.requestId;
        try {
          if (
            !rid &&
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
          ) {
            rid = crypto.randomUUID();
          }
        } catch (_) {}

        const rpcArgs = {
          p_code: tournamentCode,
          p_payload: args.payload,
          p_kind: String(args.kind || DEFAULT_TOURNAMENT_KIND),
          p_source: String(args.source || DEFAULT_SOURCE),
        };
        if (rid) rpcArgs.p_request_id = rid;
        if (actorId) rpcArgs.p_actor = actorId;
        if (tenantId) rpcArgs.p_tenant_id = tenantId;
        // Try multiple RPC arg-shapes to survive DB signature drift.
        const rpcRes = await __rpcUpsertTournamentBootstrapWithAuditBestEffort(
          supabase,
          {
            code: tournamentCode,
            kind: args.kind,
            payload: args.payload,
            source: args.source,
            requestId: rid,
            actorId,
            tenantId,
          },
        );
        const data = rpcRes.ok ? rpcRes.data : null;
        const error = rpcRes.ok ? null : rpcRes.error;

        // FALLBACK: if RPC is broken (e.g. undefined_column) or blocked,
        // write directly to tournaments_bootstrap so the app still works.
        if (error && __isSchemaMismatchError(error)) {
          try {
            const r = await __tableUpsertTournamentBootstrapBestEffort(
              supabase,
              {
                code: tournamentCode,
                kind: String(args.kind || DEFAULT_TOURNAMENT_KIND),
                payload: args.payload,
                actorId,
                tenantId,
              },
            );
            if (r?.ok)
              return {
                ok: true,
                code: tournamentCode,
                eventId: null,
                fallback: `table-upsert:${r.table}`,
              };
            console.warn(
              `[persistence] Fallback table upsert failed for tournament "${tournamentCode}":`,
              r?.error || r,
            );
          } catch (e) {
            console.warn(
              `[persistence] Exception during fallback upsert for tournament "${tournamentCode}":`,
              e,
            );
          }
        }

        if (error) {
          // PG_ERROR_UNDEFINED_COLUMN = undefined_column (SQL function/table schema mismatch)
          // In Phase 0/1 we can safely fall back to a plain upsert on tournaments_bootstrap by code.
          console.warn(
            "[Remote] upsertTournamentBootstrapWithAuditRemote failed:",
            error,
          );

          try {
            const r = await __tableUpsertTournamentBootstrapBestEffort(
              supabase,
              {
                code: tournamentCode,
                kind: String(args.kind || DEFAULT_TOURNAMENT_KIND),
                payload: args.payload,
                actorId,
                tenantId,
              },
            );
            if (r?.ok)
              return {
                ok: true,
                code: tournamentCode,
                eventId: null,
                fallback: `table-upsert:${r.table}`,
              };
            return {
              ok: false,
              reason: "db-error",
              error: r?.error || r,
              fallback: "table-upsert",
            };
          } catch (e) {
            return {
              ok: false,
              reason: "db-error",
              error,
              fallback: "table-upsert-exception",
            };
          }
        }

        const row = Array.isArray(data) ? data[0] : data;
        return {
          ok: !!row?.ok,
          code: row?.code || tournamentCode,
          eventId: row?.event_id || null,
        };
      } catch (e) {
        console.warn(
          "[Remote] upsertTournamentBootstrapWithAuditRemote exception:",
          e,
        );
        return { ok: false, reason: "exception" };
      }
    },
    {
      kind: String(kind || DEFAULT_TOURNAMENT_KIND),
      payload,
      source: String(source || DEFAULT_SOURCE),
      requestId,
    },
    { debounceMs: DEFAULT_DEBOUNCE_MS },
  );
}
// --- Best-effort immediate flush hook (used by UI to persist critical changes like match results)
if (typeof globalThis !== "undefined" && !globalThis.__tt_persistNow) {
  globalThis.__tt_persistNow = async function __tt_persistNow(meta) {
    try {
      const svc = globalThis.__tt_persistence || null;
      if (svc && typeof svc.flushNow === "function")
        return await svc.flushNow(meta);
      if (svc && typeof svc.persistNow === "function")
        return await svc.persistNow(meta);
      if (svc && typeof svc.saveNow === "function")
        return await svc.saveNow(meta);
    } catch (_) {}
  };
}
